import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { Scissors, Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';

/**
 * HaircutPresetPanel Component
 * 
 * Displays preset haircut buttons filtered by gender
 * Clicking a preset only updates selection (does NOT trigger generation)
 */

const HaircutPresetPanel = ({ 
  presets, 
  selectedGender, 
  onPresetSelect, 
  disabled = false,
  loading = false,
  selectedPresetId = null
}) => {
  const { t } = useTranslation('admin');
  // Preload preset preview images so the grid renders immediately without relying on
  // browser lazy-loading placeholders (which can trigger "[Intervention]" logs).
  useEffect(() => {
    if (!Array.isArray(presets) || presets.length === 0) return;
    const urls = presets
      .map((p) => p?.localPreviewImage || p?.previewImage)
      .filter(Boolean);

    urls.forEach((src) => {
      try {
        const img = new Image();
        img.decoding = 'async';
        img.src = src;
      } catch (e) {
        // ignore
      }
    });
  }, [presets]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-zinc-400" />
      </div>
    );
  }

  if (!presets || presets.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-zinc-400">
        <Scissors className="h-12 w-12 mb-3 opacity-50" />
        <p className="text-sm">{t('admin.no_presets_available')}</p>
        <p className="text-xs mt-1">{t('admin.try_different_gender')}</p>
      </div>
    );
  }

  return (
    <div className={`relative ${disabled ? 'pointer-events-none' : ''}`}>
      {/* Overlay when disabled */}
      {disabled && (
        <div className="absolute inset-0 bg-zinc-100/80 dark:bg-zinc-900/80 backdrop-blur-sm rounded-xl z-10 flex items-center justify-center">
          <div className="text-center px-4">
            <p className="text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-1">
              {t('admin.custom_request_active')}
            </p>
            <p className="text-xs text-zinc-600 dark:text-zinc-400">
              {t('admin.clear_custom_text')}
            </p>
          </div>
        </div>
      )}
      
      <div className="grid grid-cols-2 gap-3 max-h-[600px] overflow-y-auto p-1">
        {presets.map((preset) => {
        const isSelected = selectedPresetId === preset.id;
        
        return (
          <motion.button
            key={preset.id}
            onClick={() => onPresetSelect(preset)}
            disabled={disabled}
            className={`
              relative overflow-hidden rounded-xl transition-all duration-200
              ${isSelected 
                ? 'ring-2 ring-[#7d6633] ring-offset-2 dark:ring-offset-zinc-900' 
                : 'hover:ring-2 hover:ring-zinc-300 dark:hover:ring-zinc-600'
              }
              ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
            `}
            whileHover={!disabled ? { scale: 1.03 } : {}}
            whileTap={!disabled ? { scale: 0.97 } : {}}
          >
            {/* Image */}
            <div className="aspect-square w-full overflow-hidden bg-zinc-200 dark:bg-zinc-800">
              <img
                src={preset.localPreviewImage || preset.previewImage}
                alt={preset.name}
                className="w-full h-full object-cover"
                loading="eager"
                decoding="async"
                fetchPriority={isSelected ? 'high' : 'low'}
                width={256}
                height={256}
                data-fallback={preset.previewImage || ''}
                onError={(e) => {
                  const fallback = e.currentTarget.dataset.fallback;
                  if (fallback && e.currentTarget.src !== fallback) {
                    e.currentTarget.src = fallback;
                  } else {
                    // If both fail, use a placeholder
                    e.currentTarget.style.display = 'none';
                    e.currentTarget.parentElement.style.backgroundColor = '#7d6633';
                    e.currentTarget.parentElement.style.opacity = '0.2';
                  }
                }}
              />
            </div>

            {/* Overlay with name */}
            <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 via-black/50 to-transparent p-3">
              <p className="text-white text-sm font-medium text-center leading-tight">
                {preset.name}
              </p>
            </div>

            {/* Selected indicator */}
            {isSelected && (
              <div className="absolute top-2 right-2 bg-[#7d6633] text-white rounded-full p-1.5">
                <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
              </div>
            )}
          </motion.button>
        );
      })}
      </div>
    </div>
  );
};

export default HaircutPresetPanel;

